import React, { FC } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';

import { ThemeProps } from '@styles/theme';
import { flex } from '@styles/resources/mixins';
import ArrowRightIcon from '@assets/arrow-right.svg';
import PaginationEndIcon from '@assets/pagination-end.svg';
import { PaginationProps } from '../Table';
import { messages } from '@shared/components/components.messages';
import { useTheme } from '@mui/material/styles';

type Props = {
    className?: string,
    paginationStatus: PaginationProps,
    goToPage: Function,
};

const useStyles = createUseStyles((theme: ThemeProps) => ({
    body: {
        ...flex('row', 'space-between', 'center'),
        width: '100%',
        minHeight: 68,
        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        backgroundColor: theme.colors.surface_elevated,
        borderTop: `1px solid ${theme.colors.grey_light}`,
        [theme.down('tablet')]: {
            padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
            justifyContent: 'center',
        },
        // In card mode the table chrome is gone: render the pager as its own
        // rounded bar matching the cards.
        [theme.down('mobile')]: {
            border: `1px solid ${theme.colors.grey_light}`,
            borderRadius: theme.radius * 2,
        },
    },
    edgeButton: {
        [theme.down('mobile')]: {
            display: 'none',
        },
    },
    button: {
        border: 'none',
        background: 'none',
        padding: 0,
        height: 36,
        width: 36,
        [theme.touch]: {
            height: 44,
            width: 44,
        },
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.radius / 1.5,
        cursor: 'pointer',

        '& img': {
            filter: theme.isDark ? 'invert(0.75)' : 'none',
        },

        '&:hover:not(:disabled)': {
            backgroundColor: theme.colors.icon_background,
        },

        '&:disabled': {
            opacity: .3,
            cursor: 'default',
        },
    },
    icon: {
        transformOrigin: 'center',
    },
    reverted: {
        transform: 'scale(-1, 1)',
    },
    container: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    boldText: {
        fontSize: 16,
        fontFamily: theme.font.secondary,
        fontWeight: 'bold',
        color: theme.colors.black,
    },
    text: {
        color: theme.colors.grey_text,
        fontSize: 16,
        fontFamily: theme.font.secondary,
    },
    summary: {
        justifyContent: 'flex-start',
        minWidth: 0,
        flex: '1 1 220px',
        [theme.down('tablet')]: {
            justifyContent: 'center',
        },
    },
    pageControls: {
        justifyContent: 'flex-end',
        flex: '1 1 260px',
        [theme.down('tablet')]: {
            justifyContent: 'center',
        },
    },
    pageButtons: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    pageSummary: {
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: theme.spacing(1),
        margin: `0 ${theme.spacing(1)}`,
    },
}));

export const TablePagination: FC<Props> = ({
    paginationStatus,
    goToPage,
    className,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const {
        currentPage: pageIndex, limit: pageLimit, totalEntities, totalPages,
    } = paginationStatus;

    const canPreviousPage = () => pageIndex !== 0;
    const canNextPage = () => pageIndex < totalPages - 1;

    const safeTotalPages = totalPages || 0;
    const currentStart = totalEntities === 0 ? 0 : pageIndex * pageLimit + 1;
    const currentEnd = totalEntities === 0
        ? 0
        : canNextPage()
            ? (pageIndex + 1) * pageLimit
            : totalEntities;
    const currentPageLabel = totalEntities === 0 ? 0 : pageIndex + 1;

    return (
        <div className={classNames(styles.body, className)}>
            <div className={classNames(styles.container, styles.summary)}>
                <span className={styles.boldText}>
                    {currentStart} to {currentEnd}
                </span>
                <span className={styles.text}>
                    of {totalEntities}
                </span>
            </div>

            <div className={classNames(styles.container, styles.pageControls)}>
                <div className={styles.pageButtons}>
                    <button
                        className={classNames(styles.button, styles.reverted, styles.edgeButton)}
                        onClick={() => goToPage(0)}
                        disabled={!canPreviousPage()}
                        aria-label="Go to first page"
                        title="Go to first page"
                    >
                        <img src={PaginationEndIcon} alt="First page" />
                    </button>

                    <button
                        className={classNames(styles.button, styles.reverted)}
                        onClick={() => goToPage(pageIndex - 1)}
                        disabled={!canPreviousPage()}
                        aria-label="Go to previous page"
                        title="Go to previous page"
                    >
                        <img src={ArrowRightIcon} alt="Previous page" />
                    </button>
                </div>

                <div className={styles.pageSummary} aria-label={messages['tablePagination.summary'](currentPageLabel, safeTotalPages)}>
                    <span className={styles.boldText}>
                        Page {currentPageLabel}
                    </span>
                    <span className={styles.text}>
                        of {safeTotalPages}
                    </span>
                </div>

                <div className={styles.pageButtons}>
                    <button
                        className={styles.button}
                        onClick={() => goToPage(pageIndex + 1)} disabled={!canNextPage()}
                        aria-label="Go to next page"
                        title="Go to next page"
                    >
                        <img src={ArrowRightIcon} alt="Next page" />
                    </button>

                    <button
                        className={classNames(styles.button, styles.edgeButton)}
                        onClick={() => goToPage(totalPages - 1)} disabled={!canNextPage()}
                        aria-label="Go to last page"
                        title="Go to last page"
                    >
                        <img src={PaginationEndIcon} alt="Last page" />
                    </button>
                </div>
            </div>
        </div>
    )
}
